import React, { Fragment, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { trackViewPromotion } from '@src/analytics/trackingService'
import { useTranslation } from 'react-i18next'

import { StyledCheckoutButton } from '@src/components/CheckoutModal/CheckoutButton.styles'
import { CheckoutButtonWrapperMobile } from '@src/components/CheckoutModal/CheckoutButtonWrapperMobile'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { AddOnItemCard } from './AddOnItemCard'
import { Container, Description } from './AddOnItems.styles'

import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import {
  useBasketTotals,
  UseBasketTotalsResponseData,
} from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContextOptional } from '@src/hooks/useTotals/BasketTotalsContext'
import { AsyncDataWrapper } from '@src/hooks/types'
import { useAddOnMenuGroups } from '@src/hooks/useAddOnMenuGroups/useAddOnMenuGroups'
import {
  NarrowFulfilmentMethodInputType,
  MenuItemGroupFulfillmentMethod,
} from '@src/graphql-types'
import { AddOnMenuGroupsDocument } from './queries/__generated__/AddOnMenuGroups.graphql-interface'

// TODO sorry i feel like this should already exist but i cant find it
export const ChosenFulfilmentToMenuItemGroupFulfillmentMethod: Record<
  NarrowFulfilmentMethodInputType,
  MenuItemGroupFulfillmentMethod
> = {
  [NarrowFulfilmentMethodInputType.DELIVERY]:
    MenuItemGroupFulfillmentMethod.DELIVERY,
  [NarrowFulfilmentMethodInputType.COLLECTION]:
    MenuItemGroupFulfillmentMethod.COLLECTION,
  [NarrowFulfilmentMethodInputType.TABLE]: MenuItemGroupFulfillmentMethod.TABLE,
}

export const AddOnItems: React.FC<{
  setAddOnItemsHeader: (addOnItemsHeader: string) => void
  basketTotals?: AsyncDataWrapper<UseBasketTotalsResponseData>
}> = ({ setAddOnItemsHeader, basketTotals: basketTotalsProp }) => {
  const { override } = useCheckoutRouter()
  const { t } = useTranslation('addOnItems')

  // Try to get basketTotals from context first, fallback to prop or hook
  const contextData = useBasketTotalsContextOptional()
  const basketTotalsHookResult = useBasketTotals()
  const basketTotals =
    basketTotalsProp ||
    (contextData ? contextData.basketTotals : null) ||
    basketTotalsHookResult

  const {
    outlet,
    data: { currentFulfilment },
  } = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })

  const { name: restaurantName } = outlet.restaurant

  const { data, loading } = useQuery(
    AddOnMenuGroupsDocument,
    basketTotals.data?.checkoutBasketTotals
      ? {
          variables: {
            outletId: outlet.id,
            subTotal:
              basketTotals.data.checkoutBasketTotals.nonAddOnBasketItemsTotal,
            fulfilmentMethods: [
              ChosenFulfilmentToMenuItemGroupFulfillmentMethod[
                currentFulfilment.narrowType
              ],
            ],
            endOfPrep: currentFulfilment.endOfPrep?.toISOString(),
          },
          fetchPolicy: 'cache-first',
          notifyOnNetworkStatusChange: false,
          onCompleted: data => {
            if (!data.menuItemGroupsForOutlet.length) {
              override(CheckoutRoute.PAYMENT)
            }
          },
          onError: _error => {
            override(CheckoutRoute.PAYMENT)
          },
        }
      : { skip: true }
  )

  const addOnMenuGroups = data?.menuItemGroupsForOutlet || []

  const {
    currentGroup,
    hasMoreGroups,
    nextGroup,
    totalActiveGroups,
    conflictingGroup,
  } = useAddOnMenuGroups(addOnMenuGroups, basketTotals)

  const nextPageHandler = () =>
    hasMoreGroups && totalActiveGroups > 0
      ? nextGroup()
      : override(CheckoutRoute.PAYMENT)

  useEffect(() => {
    if (currentGroup?.name) {
      setAddOnItemsHeader(currentGroup.name) // used in Checkout.tsx and CheckoutHeader.tsx
    }
  }, [currentGroup?.name, setAddOnItemsHeader])

  // currentGroup is recomputed on every basket change, so key on its stable id
  // to fire view_promotion once per group shown rather than on each basket edit.
  useEffect(() => {
    if (!currentGroup) return
    trackViewPromotion({
      promotionName: 'Add-on Items',
      affiliation: restaurantName,
      items: (currentGroup.outletMenuItems || []).map(item => ({
        itemId: item.menuItemId,
        itemName: item.name,
      })),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per group id; currentGroup/items are recomputed on basket edits but stable per id
  }, [currentGroup?.id])

  if (loading || !basketTotals.data?.checkoutBasketTotals)
    return <LoadingSpinner />

  if (!currentGroup) {
    nextPageHandler()
    return null
  }

  return (
    <Fragment>
      <Container windowHeight={window.innerHeight}>
        <Description>
          {currentGroup.description || t('offers_desc', { restaurantName })}
        </Description>
        {currentGroup.outletMenuItems.map(addOnItem => (
          <AddOnItemCard
            key={addOnItem.menuItemId}
            addOnItem={addOnItem}
            groupIsAtMaxQuantity={currentGroup.isAtMaxQuantity}
            showItemThumbnail={Boolean(currentGroup.showMenuThumbnails)}
            conflictingGroup={conflictingGroup}
          />
        ))}
      </Container>
      <CheckoutButtonWrapperMobile>
        <StyledCheckoutButton
          content={
            hasMoreGroups
              ? t('next_group', { defaultValue: 'Next' })
              : currentGroup?.hasItemsInBasket
              ? t('continue')
              : t('no_thanks')
          }
          onClick={nextPageHandler}
          dataTestId="basket-summary-button-addonitems"
        />
      </CheckoutButtonWrapperMobile>
    </Fragment>
  )
}
